import React from 'react';
import { Badge, Collapse, Tabs, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';

import DetailDrawer from '../../../../components/shared/DetailDrawer';
import { EmptyState, InlineLoading } from '../../../../components/shared';
import { fetchGxEData, GxEGene, GxEIntervention, GxESnp } from '../../../../api/geneCodes/GxEApi';
import './interventionEvidence.css';

const { Text } = Typography;

const PUBMED_LIMIT = 5;
const STALE_TIME_MS = 5 * 60 * 1000;

export interface InterventionEvidencePanelProps {
	open: boolean;
	code: string | null;
	description?: string;
	sampleId: string;
	panelName: string;
	onClose: () => void;
}

const renderGeneDetailContent = (gene: GxEGene) => {
	const tabs = [];
	if (gene.molecular_details) tabs.push({ key: '1', label: <Text className="wheel-evidence__name">Molecular</Text>, children: gene.molecular_details });
	if (gene.clinical_implications) tabs.push({ key: '2', label: <Text className="wheel-evidence__name">Clinical</Text>, children: gene.clinical_implications });
	if (gene.finding) tabs.push({ key: '3', label: <Text className="wheel-evidence__name">Finding</Text>, children: gene.finding });

	if (tabs.length === 0) {
		return (
			<div className="wheel-evidence__pad">
				<Text className="wheel-evidence__prose wheel-evidence__prose--empty">No detailed information available for this gene.</Text>
			</div>
		);
	}

	if (tabs.length === 1) {
		return (
			<div className="wheel-evidence__pad">
				<div className="wheel-evidence__quote">
					<Text className="wheel-evidence__prose">{tabs[0].children}</Text>
				</div>
			</div>
		);
	}

	return (
		<Tabs
			size="small"
			className="wheel-evidence__tabs"
			items={tabs.map(tab => ({
				...tab,
				children: (
					<div className="wheel-evidence__quote">
						<Text className="wheel-evidence__prose">{tab.children}</Text>
					</div>
				),
			}))}
		/>
	);
};

const renderSNPs = (snps: GxESnp[]) => {
	if (!snps || snps.length === 0) return null;

	return (
		<div className="wheel-evidence__section">
			<Text type="secondary" className="wheel-evidence__heading">Associated SNPs</Text>
			<div className="wheel-evidence__list">
				{snps.map(snp => (
					<div key={snp.snp_name} data-testid={`wheel-evidence-snp-${snp.snp_name}`} className="wheel-evidence__row">
						<div className="wheel-evidence__row-lead">
							<Text className="wheel-evidence__name">{snp.snp_name}</Text>
							<Badge count={snp.pmid_count} />
						</div>
						<Text className="wheel-evidence__row-note">{snp.therapeutic_window || snp.finding || 'SNP evidence available'}</Text>
					</div>
				))}
			</div>
		</div>
	);
};

const renderPublications = (pmids: string[]) => {
	if (!pmids || pmids.length === 0) return null;

	return (
		<div className="wheel-evidence__section">
			<Text type="secondary" className="wheel-evidence__heading">Publications</Text>
			<div className="wheel-evidence__pmids">
				{pmids.slice(0, PUBMED_LIMIT).map(pmid => (
					<a key={pmid} href={`https://pubmed.ncbi.nlm.nih.gov/${pmid.trim()}/`} target="_blank" rel="noopener noreferrer" className="wheel-evidence__pmid-link">
						<Tag color="blue">{pmid.trim()}</Tag>
					</a>
				))}
				{pmids.length > PUBMED_LIMIT && <Text className="wheel-evidence__pmid-more">+{pmids.length - PUBMED_LIMIT} more</Text>}
			</div>
		</div>
	);
};

const renderGeneAccordion = (genes: GxEGene[]) => {
	const genesSorted = [...genes].sort((a, b) => b.pmid_count - a.pmid_count);

	const items = genesSorted.map((gene, index) => ({
		key: index.toString(),
		label: (
			<div data-testid={`wheel-evidence-gene-${gene.gene_name}`} className="wheel-evidence__gene-head">
				<Text className="wheel-evidence__gene-name">{gene.gene_name}</Text>
				<Text className="wheel-evidence__gene-count">{gene.pmid_count} studies</Text>
			</div>
		),
		children: (
			<div>
				<div className="wheel-evidence__summary">
					<Text className="wheel-evidence__prose">{gene.therapeutic_window || gene.finding || 'Evidence available'}</Text>
				</div>
				{renderGeneDetailContent(gene)}
				{renderPublications(gene.pmids)}
				{renderSNPs(gene.snps)}
			</div>
		),
	}));

	// The strongest-evidence gene opens by default; the rest stay collapsed so the drawer stays scannable.
	return <Collapse items={items} size="small" defaultActiveKey={['0']} className="wheel-evidence__accordion" />;
};

const renderStats = (intervention: GxEIntervention) => (
	<div className="wheel-evidence__stats">
		<div className="wheel-evidence__stat">
			<div className="wheel-evidence__stat-value">{intervention.gene_count}</div>
			<div className="wheel-evidence__stat-label">Genes</div>
		</div>
		{intervention.snp_count > 0 && (
			<div className="wheel-evidence__stat">
				<div className="wheel-evidence__stat-value">{intervention.snp_count}</div>
				<div className="wheel-evidence__stat-label">SNPs</div>
			</div>
		)}
		<div className="wheel-evidence__stat">
			<div className="wheel-evidence__stat-value">{intervention.total_studies}</div>
			<div className="wheel-evidence__stat-label">Studies</div>
		</div>
	</div>
);

export const InterventionEvidencePanel: React.FC<InterventionEvidencePanelProps> = ({ open, code, description, sampleId, panelName, onClose }) => {
	// Kilobytes of prose per gene — never fetched until the practitioner asks for it.
	const { data, isLoading, isError } = useQuery({
		queryKey: ['gxe-evidence', sampleId, panelName],
		queryFn: () => fetchGxEData(panelName, sampleId),
		enabled: open && !!sampleId && !!panelName,
		staleTime: STALE_TIME_MS,
	});

	const intervention = (data?.interventions ?? []).find(item => item.intervention_code === code);
	const title = description || intervention?.intervention_name || code || 'Evidence';

	const renderBody = () => {
		if (isLoading) {
			return <InlineLoading minHeight={120} message="Loading evidence…" />;
		}

		if (isError) {
			return (
				<div data-testid="wheel-evidence-error" className="wheel-evidence__error">
					Evidence unavailable for this intervention.
				</div>
			);
		}

		if (!intervention || !intervention.genes || intervention.genes.length === 0) {
			return (
				<div data-testid="wheel-evidence-empty">
					<EmptyState title="No gene-level evidence" description={`No published gene evidence for this intervention in ${panelName}.`} />
				</div>
			);
		}

		return (
			<div>
				{intervention.description && <Text className="wheel-evidence__prose wheel-evidence__prose--muted wheel-evidence__summary">{intervention.description}</Text>}
				{renderStats(intervention)}
				{renderGeneAccordion(intervention.genes)}
			</div>
		);
	};

	return (
		<DetailDrawer open={open} onClose={onClose} width={420} title={title} subtitle={panelName}>
			<div data-testid="wheel-intervention-evidence-panel">{renderBody()}</div>
		</DetailDrawer>
	);
};

export default InterventionEvidencePanel;
